/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { IViewportDatasource, IViewportDatasourceParams } from 'ag-grid-community';
import { Observable, ReplaySubject } from 'rxjs';

import {
  GridViewport,
  GridViewportControl,
  GridViewportRange,
  GridViewportRowData,
} from './grid-viewport.interfaces';

export function createImmutableGridViewport(): ImmutableGridViewport<any> {
  return new ImmutableGridViewport();
}

export class ImmutableGridViewport<T> implements GridViewport, IViewportDatasource, GridViewportControl {
  private _range = new ReplaySubject<GridViewportRange>(1);
  private _currentRange: GridViewportRange;
  private dsParams: IViewportDatasourceParams | null = null;

  get range(): Observable<GridViewportRange> {
    return this._range.asObservable();
  }

  get currentRange(): GridViewportRange {
    return this._currentRange;
  }

  connect(_control: GridViewportControl): void {
    // Kept for backward compatibility with callers expecting `GridViewport.connect`.
  }

  disconnect(): void {
    this.dsParams = null;
  }

  init(params: IViewportDatasourceParams): void {
    this.dsParams = params;
  }

  setViewportRange(firstRow: number, lastRow: number): void {
    const range: GridViewportRange = { start: firstRow, end: lastRow };
    this._currentRange = range;
    this._range.next(range);
  }

  setVisibleRange(range: GridViewportRange): void {
    this._currentRange = range;
    this._range.next(range);
  }

  setRowData(rowData: GridViewportRowData<T>): void {
    if (!this.dsParams) {
      return;
    }
    this.dsParams.setRowData(rowData as { [key: number]: any });
  }

  setRowCount(count: number): void {
    if (!this.dsParams) {
      return;
    }
    this.dsParams.setRowCount(count);
  }

  getRowNode(id: string): import('ag-grid-community').IRowNode {
    if (!this.dsParams) {
      throw new Error('ImmutableGridViewport: viewport datasource not initialised');
    }
    return this.dsParams.getRow(Number(id));
  }

  insertRow(_index: number, _data: any): import('ag-grid-community').IRowNode {
    throw new Error('ImmutableGridViewport: insertRow is not supported with IViewportDatasource');
  }

  removeRow(_index: number): void {
    throw new Error('ImmutableGridViewport: removeRow is not supported with IViewportDatasource');
  }
}
